import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { Card, CardContent } from '../../../shared/components/ui';
import { buildGeographyData } from '../../../shared/services/insightsApi';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{label}</p>
      <div className="space-y-1">
        <div className="flex justify-between gap-6">
          <span className="text-gray-500 dark:text-gray-400">Projects</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{d?.count}</span>
        </div>
        {d?.avgCostPerSf > 0 && (
          <div className="flex justify-between gap-6">
            <span className="text-gray-500 dark:text-gray-400">Avg $/SF</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">${d?.avgCostPerSf?.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * GeographyChart — horizontal bar chart showing project count by state.
 * Props: estimates (Array)
 */
export function GeographyChart({ estimates }) {
  const data = useMemo(() => buildGeographyData(estimates), [estimates]);
  const chartHeight = Math.max(200, data.length * 44);

  return (
    <Card className="shadow-sm">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Geographic Distribution</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Project count by state</p>
          </div>
          {data.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
              {data.length} {data.length === 1 ? 'state' : 'states'}
            </span>
          )}
        </div>
      </div>

      <CardContent className="pt-4 pb-2">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No location data</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Estimates with state abbreviations in location will appear here
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 0, right: 56, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                type="category"
                dataKey="state"
                width={40}
                tick={{ fontSize: 12, fill: '#374151', fontWeight: 600 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f0fdf4' }} />
              <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} maxBarSize={28}>
                <LabelList
                  dataKey="count"
                  position="right"
                  style={{ fontSize: '11px', fill: '#6b7280', fontWeight: 500 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default GeographyChart;
